import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  activateLicense,
  deactivateLicense,
  fetchCompanyLicenses,
  fetchLicenseTypes,
  fetchSellers,
  onDisablePortfolio,
  setEditing,
  upgradeLicense,
  updateLicense,
} from '~actions'; // eslint-disable-line

import {
  companyHasActiveLicense,
  getActionLog,
  getActiveCompany,
  getActiveCompanyLicenses,
  getActiveCompanyLicensesFetchState,
  getCurrentUser,
  getLicenseSellers,
  getLicenseTypes,
} from '../../selectors';

import ActionLog from '../actionlog/actionlog.container';
import ControlButtons from '../dashboard/ControlButtons';
import LicenseControls from './LicenseControls';
import LicenseInfo from './LicenseInfo';
import LicenseUpdateControls from './LicenseUpdateControls';
import Padder from '../../components/Padder';
import LoadingIndicator from '../../components/LoadingIndicator';
import './styles/LicenseControlPanel.scss';
import PortfolioEnabled from '../paid/PortfolioEnabled';

import messages from './messages/LicenseControlPanel';

const propTypes = {
  actionInProgress: PropTypes.bool.isRequired,
  actionLog: PropTypes.array.isRequired,
  activeLicenseExists: PropTypes.bool,
  activateLicense: PropTypes.func.isRequired,
  company: PropTypes.object.isRequired,
  deactivateLicense: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  fetchCompanyLicenses: PropTypes.func.isRequired,
  fetchLicenseTypes: PropTypes.func.isRequired,
  fetchSellers: PropTypes.func.isRequired,
  fetchState: PropTypes.string,
  licenses: PropTypes.array,
  licenseTypes: PropTypes.array,
  onDisablePortfolio: PropTypes.func.isRequired,
  sellers: PropTypes.array,
  setEditing: PropTypes.func.isRequired,
  upgradeLicense: PropTypes.func.isRequired,
  updateLicense: PropTypes.func.isRequired,
  user: PropTypes.object,
};

const defaultProps = {
  activeLicenseExists: false,
  editMode: false,
  fetchState: null,
  licenses: null,
  licenseTypes: [],
  sellers: [],
};

class LicenseControlPanel extends Component {
  constructor(props) {
    super(props);

    // If the current license updating is active (continuous, contact person,
    // reference number), we want to hide controls for upgarding or deactivation
    // and vice versa. If neither is active, display both.
    this.UI_STATE_CHANGE = 'change';
    this.UI_STATE_IDLE = 'idle';
    this.UI_STATE_UPDATE = 'update';

    this.state = { uiState: this.UI_STATE_IDLE };
  }

  componentDidMount()  {
    this.props.fetchLicenseTypes();
    this.props.fetchSellers();
    const{ company } = this.props;
    if (company) {
      if (company && company.id) {
        this.props.fetchCompanyLicenses({
          country: company.country,
          identifier: company.identifier,
        });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const{ company } = this.props;
    if (company) {
      if (!prevProps.company || prevProps.company.id !== company.id) {
        this.props.fetchCompanyLicenses({
          country: company.country,
          identifier: company.identifier,
        });
      }
    }
  }

  toggleLicenseUpdateState = isActive => {
    if (isActive) {
      this.setState({ uiState: this.UI_STATE_UPDATE });
    } else {
      this.setState({ uiState: this.UI_STATE_IDLE });
    }
  }

  toggleLicenseControlState = isActive => {
    if (isActive) {
      this.setState({ uiState: this.UI_STATE_CHANGE });
    } else {
      this.setState({ uiState: this.UI_STATE_IDLE });
    }
  }

  get currentLicense() {
    const { licenses } = this.props;
    let currentLicense = null;
    if (licenses !== null) {
      currentLicense = licenses.find(
        l => ['ordered', 'pending'].indexOf(
          l.toComparable('license.status')
        ) !== -1
      );
    }
    return currentLicense;
  }

  activate = ({ license }) => {
    const{ company: { country, identifier }} = this.props;
    const currentLicense = this.currentLicense;
    if (currentLicense) {
      this.upgrade({ country, identifier, license });
    } else {
      this.props.activateLicense({
        country,
        identifier,
        license: license.data,
      });
    }
  }

  upgrade = ({ country, identifier, license }) => {
    const currentLicense = this.currentLicense;
    if (currentLicense) {
      this.props.upgradeLicense({
        country,
        identifier,
        license: currentLicense.data,
        newLicense: license.data
      });
    }
  }

  get userIsAdmin() {
    // These checks are only used to alter UI, actual data security is done
    // server side...same logic thou.
    const ADMIN_LEVELS = ['license_admin', 'super_user'];
    const { user } = this.props;
    if (!user) {
      return false;
    }
    const matchinLevels = user.access_levels.filter(
      ual => ADMIN_LEVELS.indexOf(ual) !== -1
    );
    if (matchinLevels.length) {
      // If user is admin, there's no need to check anything regarding the
      // currently active license.
      return true;
    }
    return false;
  }

  get userIsRetailer() {
    // These checks are only used to alter UI, actual data security is done
    // server side...same logic thou.
    const RETAILER_LEVELS = ['license_retailer'];
    const { user } = this.props;
    if (!user) {
      return false;
    }
    const matchinLevels = user.access_levels.filter(
      ual => RETAILER_LEVELS.indexOf(ual) !== -1
    );
    if (!matchinLevels.length) {
      return false;
    }
    const currentLicense = this.currentLicense;
    if (!currentLicense) {
      // If there is no active license for the company and the user is retailer
      // (super_user) then this check is OK.
      return true;
    }
    // However if the company has an active license, the user is qualified
    // retailer only if they've sold the license in the first place.
    const {
      retailer: {
        businessId: retailerBusinessId,
        countryCode: retailerCountryCode,
      }
    } = currentLicense;
    if (retailerBusinessId === user.company.identifier
      && retailerCountryCode === user.company.country) {
      return true;
    }
    return false;
  }

  get userCanAccess() {
    // If currentLicense is undefined (not found) but active license
    // exists (paid features are enabled), then current user is not
    // authorized to access the license.
    return !(
      this.currentLicense === undefined
      && this.props.fetchState === 'idle'
      && this.props.activeLicenseExists
    );
  }

  get userCanManage() {
    // activate / deactivate / update / upgrade
    return this.userCanAccess && (this.userIsAdmin || this.userIsRetailer);
  }

  render = () => {
    const { user } = this.props;

    if (!this.userCanAccess) {
      return (
        <div styleName='LicensePanel'>
          <div styleName='wrapper'>
            <div styleName='header'>
              <FormattedMessage {...messages.title} />
            </div>
            <div styleName='body'>
              <p styleName='notice'>
                <FormattedMessage {...messages.noAccess} />
              </p>
              {/*
              If current user is super user, but there seems to not be access to
              the current license, then it must be that the paid features are
              enabled without an active license, so we want to present the user
              with the option to disable the paid features.
              */}
              {(user && user.access_levels.indexOf('super_user') !== -1) && (
                <PortfolioEnabled
                  isSuperUser
                  onDisablePortfolio={
                    () => this.props.onDisablePortfolio(this.props.company)
                  }
                />
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div styleName='LicensePanel'>
        <div styleName='wrapper'>
          <div styleName='header'>
            <FormattedMessage {...messages.title} />
          </div>
          <div styleName='body'>
            <ActionLog actionLog={this.props.actionLog} />
            {this.currentLicense ? (
              <React.Fragment>
                <LicenseInfo
                  license={this.currentLicense}
                  detailed={this.userCanManage}
                />
                {(this.props.editMode
                  && this.userCanManage
                  && this.state.uiState !== this.UI_STATE_CHANGE
                ) &&
                  <LicenseUpdateControls
                    company={this.props.company}
                    license={this.currentLicense}
                    toggleActiveState={this.toggleLicenseUpdateState}
                    updateLicense={this.props.updateLicense}
                  />
                }
              </React.Fragment>
            ) : (
              <p styleName='notice'>
                <FormattedMessage {...messages.noActive} />
              </p>
            )}
            {(this.props.editMode
              && this.userCanManage
              && this.state.uiState !== this.UI_STATE_UPDATE
            ) && (
              <React.Fragment>
                <Padder />
                <LicenseControls
                  currentLicense={this.currentLicense}
                  companyId={this.props.company.id}
                  licenseTypes={this.props.licenseTypes}
                  onActivation={this.activate}
                  onDeactivation={this.props.deactivateLicense}
                  sellers={this.props.sellers}
                  toggleActiveState={this.toggleLicenseControlState}
                  user={this.props.user}
                />
              </React.Fragment>
            )}
            {(!this.props.editMode && this.userCanManage) && (
              <React.Fragment>
                <Padder />
                <ControlButtons
                  setEditMode={this.props.setEditing}
                  editMode={this.props.editMode}
                />
              </React.Fragment>
            )}
            {(this.props.actionInProgress
              || this.props.fetchState !== 'idle') && (
              <div styleName='loader-mask'>
                <LoadingIndicator color='#000' />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

LicenseControlPanel.propTypes = propTypes;
LicenseControlPanel.defaultProps = defaultProps;

// This makes state objects available to the component via props!
function mapStateToProps(state) {
  return {
    actionInProgress: state.licenses.actionInProgress,
    actionLog: getActionLog(state, 'licenses'),
    company: getActiveCompany(state),
    fetchState: getActiveCompanyLicensesFetchState(state),
    licenses: getActiveCompanyLicenses(state),
    activeLicenseExists: companyHasActiveLicense(state),
    licenseTypes: getLicenseTypes(state),
    sellers: getLicenseSellers(state),
    user: getCurrentUser(state),
  };
}

// This adds action creators to components props
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      activateLicense,
      deactivateLicense,
      fetchCompanyLicenses,
      fetchLicenseTypes,
      fetchSellers,
      setEditing,
      onDisablePortfolio,
      upgradeLicense,
      updateLicense,
    },
    dispatch,
  );
}

export default connect(
  mapStateToProps, mapDispatchToProps
)(LicenseControlPanel);
